import React, { useEffect, useState } from 'react';
import {
  TextField,
  Button,
  FormControlLabel,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  Divider,
  Grid,
  Typography,
  Backdrop,
  CircularProgress
} from '@mui/material';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { tableCellClasses } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { showNotificationDialog } from 'redux/actions/notificationActions';
import { SAVED_SUCCESS } from 'services/CONSTANTS';
import { LabelWithControl } from 'components/common/LabelControl';
import { showErrorMessageByCode } from 'services/commonServices';
import { menuItems } from 'data/data';
import { getRolesAction } from 'redux/actions/rolesAction';
import { getUserAction, getUserReset, updatetUserRoleResetAction, updateUserRoleAction } from 'redux/actions/addUserAction';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: '1px solid #A9A9A9',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.background.default,
    fontSize: 12, // Adjust font size for header
    padding: '3px 5px', // Reduce padding for less height
    border: '1px solid #A9A9A9',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    border: '1px solid #A9A9A9',
    padding: '3px',
  },
}));
const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: '#fff',
  ...theme.typography.body2,
  // padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  fontSize: '12px',
  fontWeight: 'bold',
  padding: '5px',
  lineHeight: 'normal',
  height: '24px',

}));

export const AddUser = () => {

  const getRolesReducer = useSelector(state => state.roles);
  const addUserReducer = useSelector(state => state.addUser);

  const [rolesMaster, setRolesMaster] = useState([]);
  const [user, setUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isSaveEnabled, setSaveEnabled] = useState(false)
  const [showSpinner, setShowSpinner] = useState(false);

  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false)

  const [userRights, setUserRights] = useState([]);
  const dispatch = useDispatch();

  // const isEmailValid = (userEmail) => {
  //   return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail)
  // };
  const isEmailValid = (userEmail) => {
    return /^[^\s@]+@(morrisonsplc\.co\.uk|test\.morrisonsplc\.co\.uk)$/.test(userEmail);
  };

  useEffect(() => {
    dispatch(getRolesAction());
  }, [dispatch])

  useEffect(() => {
    if (getRolesReducer.isGetRolesLoading) {
      setShowSpinner(true);
    } else if (getRolesReducer.isGetRolesSuccess) {
      const roles = [];
  
      getRolesReducer.getRolesResponse.rolePrivilegeDetails.forEach(role => {
        const roleObj = {
          roleId: role.roleId,
          roleName: role.roleName,
          privilegeList: []
        };
  
        role.privilegeList.forEach(privilege => {
          privilege.pages.forEach(x => {
            if (menuItems[x] && !roleObj.privilegeList.includes(menuItems[x].name)) {
              roleObj.privilegeList.push(menuItems[x].name);
            }
          });
        });
  
        roles.push(roleObj); 
      });
  
      // Sort roles based on roleId (assuming roleId is a number)
      roles.sort((a, b) => a.roleId - b.roleId);
  
      setRolesMaster(roles);
      setShowSpinner(false);
    } else if (getRolesReducer.isGetRolesError) {
      setShowSpinner(false);
      const { errTitle, errContent } = showErrorMessageByCode(getRolesReducer.getRolesResponse);
      dispatch(showNotificationDialog(errTitle, errContent, 'Ok'));
      
    }
  }, [getRolesReducer, dispatch]);

  useEffect(() => {
    if (isEmailValid(email)) {
      if (user && user.email === email) {
        setIsSubmitEnabled(false);
        setIsSubmitted(true);
      } else {
        setIsSubmitEnabled(true);
      }
    } else {
      setIsSubmitEnabled(false);
      setIsSubmitted(false);
    }
  }, [email, isSubmitted, user])

  useEffect(() => {
    if (selectedRole) {
      const rights = [];
      rolesMaster.forEach(priviledge => {
        if (priviledge.roleId === selectedRole) {
          rights.push(...priviledge.privilegeList)
        }
      })

      setUserRights(rights || []);
      setSaveEnabled(user ? user.role[0] !== selectedRole : selectedRole !== null);
    }
  }, [selectedRole, rolesMaster, user])

  const createNewUser = () => {
    const splitEmail = email.split("@");
    const splitName = splitEmail[0].split(".");
    const firstName = splitName[0];
    const lastName = splitName.length > 1 ? splitName[1] : splitName[0];
    setUser({
      email: email,
      firstName: firstName,
      lastName: lastName,
      role: [0]
    });
  }

  useEffect(() => {
    if (addUserReducer.isUpdateUserRoleLoading) {
      setShowSpinner(true);
    } else if (addUserReducer.isGetUserLoading) {
      setShowSpinner(true);
      setSelectedRole(null);
      setIsSubmitted(false);
    } else if (addUserReducer.isGetUserSuccess) {
      setUser(addUserReducer.getUserResponse);
      setSelectedRole(addUserReducer.getUserResponse.role);
      setIsSubmitted(true);
      setShowSpinner(false);
      dispatch(getUserReset());
    } else if (addUserReducer.isGetUserError) {
      
      const { response } = addUserReducer.getUserResponse;
      if (response.status === 404) {
        setSelectedRole(0);
        setIsSubmitted(true);
      } else {
        const { errTitle, errContent } = showErrorMessageByCode(addUserReducer.getUserResponse);
        dispatch(showNotificationDialog(errTitle, errContent, 'Ok'));
      }
      dispatch(getUserReset());
      setShowSpinner(false);

    } else if (addUserReducer.isUpdateUserRoleSuccess) {
      setShowSpinner(false);
      dispatch(updatetUserRoleResetAction());
      dispatch(getUserAction({userId: email}));
      dispatch(showNotificationDialog("Saved Successfully", SAVED_SUCCESS, "Done"));
    } else if (addUserReducer.isUpdateUserRoleError) {
      setShowSpinner(false);
      const { errTitle, errContent } = showErrorMessageByCode(addUserReducer.updateUserRoleResponse);
      dispatch(showNotificationDialog(errTitle, errContent, 'Ok'));
    }
  }, [addUserReducer, dispatch, email])


  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setIsSubmitted(false)
    setEmail(newEmail);
  };

  const handleSubmit = () => {
    createNewUser();
    const request = {
      userId: email
    }
    dispatch(getUserAction(request));
  };


  const handleRoleChange = (value) => {
    const roleVal = parseInt(value);
    setSelectedRole(roleVal);
  };

  const handleSave = () => {
    const request = {
      userId: email,
      params: {
        ...user,
        role: selectedRole
      }
    }
    dispatch(updateUserRoleAction(request));
  };

  return (
    <div className="App mainPageStyling">
      {showSpinner && (
        <Backdrop
          sx={{
            color: "#fff",
            flexDirection: "column",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open={true}
        >
          <CircularProgress color="inherit" />

          <Typography variant="h6" sx={{ marginTop: 2 }}>
            Loading...
          </Typography>
        </Backdrop>
      )}
      <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
        Manage User
      </Typography>
      <div style={{ marginBottom: '10px' }} className='labelControl roleControl'>
        <Grid className="paddingTop0">
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <LabelWithControl label="User Email" required>
                <TextField
                 className='userEmail'
                  placeholder="User Email"
                  variant="outlined"
                  value={email}
                  onChange={handleEmailChange}
                  error={!isEmailValid(email) && email !== ''}
                  helperText={!isEmailValid(email) && email !== '' ? 'Incorrect email format, please enter correct email id.' : ''}
                 
                />
              </LabelWithControl>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!isSubmitEnabled}
                sx={{ width: '80px', ml: 2, height: '25px', padding: '0 10px', marginTop:'18px', fontSize: '12px' }}
              >
                Submit
              </Button>
            </Box>


            {isSubmitted && (
              <>
                <Divider sx={{ my: 1 }} />
                <Grid container spacing={1} mt={1}>
                  <Grid item xs={6} sm={6} md={3}>
                    <Table>
                      <TableHead>
                        <StyledTableCell>Roles</StyledTableCell>
                      </TableHead>
                      <TableBody>
                        {
                          rolesMaster.map((privilege) =>
                            <PrivilegeRow key={privilege.roleId} privilege={privilege} selectedRole={selectedRole} onRoleChange={(x) => handleRoleChange(x)} />)
                        }
                      </TableBody>
                    </Table>
                  </Grid>
                  <Grid item xs={6} sm={6} md={9}>
                    <Table>
                      <TableHead>
                        <StyledTableCell>Privileges (User can access the following pages)</StyledTableCell>
                      </TableHead>
                      <TableBody>
                        <StyledTableRow>

                          <StyledTableCell>
                            <div className='stackStyle'>
                              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }} useFlexGap>
                                {userRights.map((right, index) => (
                                  <Item key={index}>{right}</Item>
                                ))}
                              </Stack>
                            </div>
                          </StyledTableCell>


                        </StyledTableRow>
                      </TableBody>
                    </Table>
                  </Grid>
                </Grid>
                <Box sx={{ mt: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!isSaveEnabled}
                    sx={{ width: '100px', height: '25px', fontSize: '12px' }}
                  >
                    Save
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Grid>
      </div>
    </div>
  );
};

const PrivilegeRow = ({ privilege, selectedRole, onRoleChange }) => {

  const handleRoleChange = (event) => {
    const { value } = event.target;
    onRoleChange(value)
  }
  return (
    <StyledTableRow>
      <StyledTableCell className='rolesTableStyle'>
        <FormControlLabel
          control={
            <Radio
              name="role"
              value={privilege.roleId}
              checked={privilege.roleId === selectedRole}
              onChange={handleRoleChange}
              className="radioStyle"
            />
          }
          label={privilege.roleName}
        />
      </StyledTableCell>
    </StyledTableRow>
  )
}
